import { useState } from 'react'
import { Alert, Badge, Button, Card, Group, Modal, NumberInput, SegmentedControl, SimpleGrid, Stack, Text, TextInput, Title } from '@mantine/core'
import { Link, useNavigate, useParams } from 'react-router-dom'
import backend from '../../backend/backend'
import DataList from '../../components/shared/DataList'
import Money from '../../components/shared/Money'
import PageHeader from '../../components/shared/PageHeader'
import { ApprovalChecklist, DocumentUploader, ReasonModal } from '../../components/shared/Pieces'
import StatusBadge from '../../components/shared/StatusBadge'
import { useAction, useCan, useResource, useT } from '../../hooks'
import Message from '../../i18n/Message'
import { keys, loadApplication, loadApplications } from '../../store/dataActionCreators'
import { displayPhone, formatDate, formatDateTime, fullName, maskedId, toKobo } from '../../utils/format'

const FILTERS = ['all', 'draft', 'submitted', 'in_review', 'corrections_requested', 'approved', 'rejected']

export function ApplicationsList({ base }) {
  const t = useT()
  const navigate = useNavigate()
  const canCreate = useCan('applications.submit_device') || useCan('applications.submit_cash')
  const [status, setStatus] = useState(base === '/admin' ? 'submitted' : 'all')
  const { data, loading } = useResource(keys.applications(status === 'all' ? '' : status), () => loadApplications(status === 'all' ? '' : status))
  const columns = [
    { key: 'application_no', header: 'col_ref', ref: true },
    { key: 'customer_name', header: 'col_name' },
    { key: 'product_name', header: 'col_product', mobileHidden: true },
    { key: 'principal_kobo', header: 'col_financed', render: (a) => <Money kobo={a.principal_kobo} /> },
    { key: 'status', header: 'col_status', render: (a) => <StatusBadge status={a.status} /> },
    { key: 'submitted_at', header: 'col_submitted', render: (a) => formatDate(a.submitted_at || a.created_at), mobileHidden: true },
  ]
  return (
    <>
      <PageHeader
        title="applications_title"
        actions={
          base === '/agent' &&
          canCreate && (
            <Button component={Link} to="/agent/applications/new">
              <Message id="new_application" />
            </Button>
          )
        }
      />
      <SegmentedControl
        mb="md"
        value={status}
        onChange={setStatus}
        data={FILTERS.map((f) => ({ value: f, label: f === 'all' ? t('filter_all') : t(`status_${f}`) }))}
        style={{ maxWidth: '100%', overflowX: 'auto' }}
      />
      <DataList items={data?.items} columns={columns} loading={loading} empty="applications_empty" onRowClick={(a) => navigate(`${base}/applications/${a.id}`)} />
    </>
  )
}

export function ApplicationDetail({ base }) {
  const { id } = useParams()
  const t = useT()
  const { data, reload } = useResource(keys.application(id), () => loadApplication(id))
  const { busy, run } = useAction()
  const canReview = useCan('applications.review')
  const canApprove = useCan('applications.approve')
  const canVerifyDocs = useCan('documents.verify')
  const canReveal = useCan('customers.reveal_ids')
  const canVerifyIdentity = useCan('customers.verify_identity')
  const [modal, setModal] = useState(null) // 'corrections' | 'reject' | 'cancel' | 'call' | 'adjust' | 'consent'
  const [callOutcome, setCallOutcome] = useState('confirmed')
  const [adjust, setAdjust] = useState({ term: '', deposit: '' })
  const [name, setName] = useState('')
  const [revealed, setRevealed] = useState(null)
  const navigate = useNavigate()
  if (!data) return null
  const { application: a, customer: c, documents, events, consents, calls, loan, checklist } = data
  const act = (action, body, success) => run(() => backend.applicationAction(a.id, action, body), { success, onDone: reload })
  const ownerEditable = ['draft', 'corrections_requested'].includes(a.status)
  const isAdminView = base === '/admin'

  return (
    <>
      <PageHeader
        title="application_title"
        subtitle={`${a.application_no} · ${fullName(c)}`}
        actions={
          <>
            <StatusBadge status={a.status} />
            <Button variant="default" component={Link} to={`/print/agreement/${a.id}`} target="_blank">
              <Message id="view_agreement" />
            </Button>
          </>
        }
      />
      {a.corrections_note && a.status === 'corrections_requested' && (
        <Alert color="orange" mb="md" title={<Message id="corrections_requested_title" />}>
          {a.corrections_note}
        </Alert>
      )}
      <SimpleGrid cols={{ base: 1, md: 2 }} mb="lg">
        <Card>
          <Stack gap={6}>
            <Title order={5}>
              <Message id="financing_title" />
            </Title>
            {a.price_kobo ? <Line label="quote_price" kobo={a.price_kobo} /> : null}
            {a.kind === 'device' && <Line label="quote_deposit" kobo={a.deposit_kobo} />}
            <Line label="quote_financed" kobo={a.principal_kobo} />
            <Line label="quote_interest" kobo={a.interest_kobo} />
            <Line label="quote_total" kobo={a.total_repayable_kobo} strong />
            <Text size="sm" c="dimmed">
              <Message id="quote_installments" values={{ count: a.term_months }} />
            </Text>
            {a.device_model && (
              <Text size="sm">
                {[a.device_brand, a.device_model].filter(Boolean).join(' ')}
              </Text>
            )}
          </Stack>
        </Card>
        <Card>
          <Stack gap={6}>
            <Title order={5}>
              <Message id="customer_profile" />
            </Title>
            <Text component={Link} to={`${base}/customers/${c.id}`} c="brand">
              {fullName(c)} · {c.customer_no}
            </Text>
            <Text size="sm">{displayPhone(c.phone)}</Text>
            <Text size="sm">
              <Message id="field_bvn" />: {revealed ? revealed.bvn : maskedId(c.bvn_last4)}
            </Text>
            <Text size="sm">
              <Message id="field_nin" />: {revealed ? revealed.nin : maskedId(c.nin_last4)}
            </Text>
            <Group gap="xs">
              <StatusBadge status={c.identity_status} />
              {canReveal && !revealed && (c.has_bvn || c.has_nin) && (
                <Button size="xs" variant="default" onClick={() => run(() => backend.revealIds(c.id), { onDone: setRevealed })}>
                  <Message id="reveal_ids" />
                </Button>
              )}
              {isAdminView && canVerifyIdentity && c.identity_status !== 'verified' && (
                <Button size="xs" loading={busy} onClick={() => run(() => backend.verifyIdentity(c.id, 'verified'), { success: 'identity_verified', onDone: reload })}>
                  <Message id="verify_identity" />
                </Button>
              )}
            </Group>
          </Stack>
        </Card>
      </SimpleGrid>

      {isAdminView && checklist && ['in_review', 'submitted'].includes(a.status) && (
        <Card mb="lg">
          <Title order={5} mb="sm">
            <Message id="checklist_title" />
          </Title>
          <ApprovalChecklist checklist={checklist} />
        </Card>
      )}

      <Card mb="lg">
        <Title order={5} mb="sm">
          <Message id="documents_title" />
        </Title>
        <Stack>
          {documents.map((d) => (
            <Group key={d.id} justify="space-between" wrap="wrap">
              <Group gap="xs">
                <Text size="sm" fw={500}>
                  <Message id={`doc_${d.doc_type}`} />
                </Text>
                <Text size="sm" component="a" href={backend.documentUrl(d.id)} target="_blank" rel="noreferrer" c="brand">
                  {d.original_filename}
                </Text>
                <StatusBadge status={d.status} />
              </Group>
              {canVerifyDocs && d.status === 'pending' && (
                <Group gap="xs">
                  <Button size="xs" loading={busy} onClick={() => run(() => backend.reviewDocument(d.id, { status: 'verified' }), { success: 'doc_verified', onDone: reload })}>
                    <Message id="verify" />
                  </Button>
                  <Button size="xs" color="red" variant="light" loading={busy} onClick={() => {
                    const note = window.prompt(t('reason'))
                    if (note) run(() => backend.reviewDocument(d.id, { status: 'rejected', note }), { success: 'doc_rejected', onDone: reload })
                  }}>
                    <Message id="reject" />
                  </Button>
                </Group>
              )}
            </Group>
          ))}
          {!isAdminView && ownerEditable && (
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              {['government_id', 'customer_photo', 'proof_of_address'].map((type) => (
                <DocumentUploader key={type} customerId={c.id} applicationId={a.id} docType={type} documents={documents} onChange={reload} />
              ))}
            </SimpleGrid>
          )}
        </Stack>
      </Card>

      <SimpleGrid cols={{ base: 1, md: 2 }} mb="lg">
        <Card>
          <Title order={5} mb="sm">
            <Message id="consent_title" />
          </Title>
          {consents.length === 0 ? (
            <Text size="sm" c="dimmed">
              <Message id="consent_none" />
            </Text>
          ) : (
            <Stack gap={4}>
              {consents.slice(0, 2).map((k) => (
                <Text size="sm" key={k.id}>
                  <Message id={`consent_${k.kind}`} /> · {k.accepted_name} · {formatDateTime(k.recorded_at)} ·{' '}
                  {k.agreement_hash === data.agreementHash ? <Badge color="green">{t('consent_current')}</Badge> : <Badge color="orange">{t('consent_stale')}</Badge>}
                </Text>
              ))}
            </Stack>
          )}
          {(ownerEditable || a.status === 'submitted' || a.status === 'in_review') && (
            <Button mt="sm" size="xs" variant="default" onClick={() => setModal('consent')}>
              <Message id="consent_record" />
            </Button>
          )}
        </Card>
        {isAdminView && (
          <Card>
            <Title order={5} mb="sm">
              <Message id="calls_title" />
            </Title>
            {calls.length === 0 && (
              <Text size="sm" c="dimmed">
                <Message id="calls_none" />
              </Text>
            )}
            {calls.slice(0, 3).map((k) => (
              <Text size="sm" key={k.id}>
                {formatDateTime(k.called_at)} · <Message id={`call_${k.outcome}`} />
              </Text>
            ))}
            {canReview && (
              <Button mt="sm" size="xs" variant="default" onClick={() => setModal('call')}>
                <Message id="call_log" />
              </Button>
            )}
          </Card>
        )}
      </SimpleGrid>

      <Card mb="lg">
        <Title order={5} mb="sm">
          <Message id="timeline_title" />
        </Title>
        <Stack gap={4}>
          {events.map((e) => (
            <Text size="sm" key={e.id}>
              <Text span c="dimmed">{formatDateTime(e.at)}</Text> · {e.actor_name || '—'} · <Message id={`event_${e.type}`} />
              {e.note ? ` — ${e.note}` : ''}
            </Text>
          ))}
        </Stack>
      </Card>

      <Group>
        {!isAdminView && ownerEditable && (
          <Button loading={busy} onClick={() => act('submit', {}, 'application_submitted')}>
            <Message id={a.status === 'corrections_requested' ? 'resubmit' : 'submit_application'} />
          </Button>
        )}
        {isAdminView && canReview && a.status === 'submitted' && (
          <Button loading={busy} onClick={() => act('start-review', {}, 'review_started')}>
            <Message id="start_review" />
          </Button>
        )}
        {isAdminView && canApprove && a.status === 'in_review' && (
          <>
            <Button color="green" loading={busy} onClick={() => run(() => backend.applicationAction(a.id, 'approve', {}), { success: 'application_approved', onDone: reload })}>
              <Message id="approve" />
            </Button>
            <Button variant="default" onClick={() => { setAdjust({ term: String(a.term_months), deposit: String(a.deposit_kobo / 100) }); setModal('adjust') }}>
              <Message id="adjust_terms" />
            </Button>
            <Button variant="light" color="orange" onClick={() => setModal('corrections')}>
              <Message id="request_corrections" />
            </Button>
            <Button variant="light" color="red" onClick={() => setModal('reject')}>
              <Message id="reject" />
            </Button>
          </>
        )}
        {['draft', 'submitted', 'corrections_requested', 'approved'].includes(a.status) && (!loan || loan.status === 'awaiting_release') && (
          <Button variant="subtle" color="gray" onClick={() => setModal('cancel')}>
            <Message id="cancel_application" />
          </Button>
        )}
        {loan && (
          <Button variant="default" onClick={() => navigate(`${base}/loans/${loan.id}`)}>
            <Message id="open_loan" values={{ no: loan.loan_no }} />
          </Button>
        )}
      </Group>

      <ReasonModal opened={modal === 'corrections'} title="request_corrections" label="note" busy={busy} onClose={() => setModal(null)}
        onSubmit={async (note) => { await act('request-corrections', { note }, 'corrections_sent'); setModal(null) }} />
      <ReasonModal opened={modal === 'reject'} title="reject" label="note" busy={busy} onClose={() => setModal(null)}
        onSubmit={async (note) => { await act('reject', { note }, 'application_rejected'); setModal(null) }} />
      <ReasonModal opened={modal === 'cancel'} title="cancel_application" label="note" required={false} busy={busy} onClose={() => setModal(null)}
        onSubmit={async (note) => { await act('cancel', { note }, 'application_cancelled'); setModal(null) }} />

      <Modal2 opened={modal === 'call'} title="call_log" onClose={() => setModal(null)}>
        <SegmentedControl orientation="vertical" fullWidth value={callOutcome} onChange={setCallOutcome}
          data={['confirmed', 'no_answer', 'declined', 'mismatch'].map((o) => ({ value: o, label: t(`call_${o}`) }))} />
        <Button mt="md" loading={busy} onClick={async () => { await run(() => backend.applicationAction(a.id, 'confirmation-calls', { outcome: callOutcome }), { success: 'call_logged', onDone: reload }); setModal(null) }}>
          <Message id="save" />
        </Button>
      </Modal2>
      <Modal2 opened={modal === 'adjust'} title="adjust_terms" onClose={() => setModal(null)}>
        <Stack>
          <Alert color="orange"><Message id="adjust_warning" /></Alert>
          <NumberInput label={t('field_term')} min={1} max={3} value={adjust.term} onChange={(v) => setAdjust({ ...adjust, term: v })} />
          {a.kind === 'device' && <TextInput label={t('field_deposit')} value={adjust.deposit} onChange={(e) => setAdjust({ ...adjust, deposit: e.currentTarget.value })} />}
          <Button loading={busy} onClick={async () => {
            const body = { termMonths: Number(adjust.term) }
            if (a.kind === 'device') body.depositKobo = toKobo(adjust.deposit)
            await run(() => backend.applicationAction(a.id, 'adjust', body), { success: 'terms_adjusted', onDone: reload })
            setModal(null)
          }}>
            <Message id="save" />
          </Button>
        </Stack>
      </Modal2>
      <Modal2 opened={modal === 'consent'} title="consent_record" onClose={() => setModal(null)}>
        <Stack>
          <Text size="sm"><Message id="consent_hint" /></Text>
          <TextInput label={t('consent_name')} value={name} onChange={(e) => setName(e.currentTarget.value)} />
          <Button disabled={!name.trim()} loading={busy} onClick={async () => {
            await run(() => backend.applicationAction(a.id, 'consents', isAdminView ? { accepted_name: name.trim(), method: 'phone' } : { accepted_name: name.trim() }), { success: 'consent_recorded', onDone: reload })
            setModal(null)
          }}>
            <Message id="consent_record" />
          </Button>
        </Stack>
      </Modal2>
    </>
  )
}

function Modal2({ opened, title, onClose, children }) {
  return (
    <Modal opened={opened} onClose={onClose} title={<Message id={title} />} centered>
      {children}
    </Modal>
  )
}

function Line({ label, kobo, strong }) {
  return (
    <Group justify="space-between">
      <Text size="sm" fw={strong ? 600 : 400}>
        <Message id={label} />
      </Text>
      <Money kobo={kobo} fw={strong ? 600 : 400} />
    </Group>
  )
}
